import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { createLogger } from '../../lib/logger';

const log = createLogger('auth');

interface LoginFormProps {
  onSignUpClick?: () => void;
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSignUpClick, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [smartSuggestion, setSmartSuggestion] = useState<string | null>(null);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      log.debug('LoginForm: 開始登入流程，email:', email);
      const { user, error: signInError } = await signIn(email, password);
      
      log.debug('LoginForm: signIn 結果:', {
        hasUser: !!user,
        hasError: !!signInError,
        errorMessage: signInError?.message
      });

      if (signInError) {
        log.debug('LoginForm: 登入失敗，顯示錯誤');
        setError(getErrorMessage(signInError));
      } else if (user) {
        log.info('LoginForm: 登入成功，準備跳轉');
        // 加入短暫延遲確保狀態更新完成
        setTimeout(() => {
          if (onSuccess) {
            log.debug('LoginForm: 調用 onSuccess 回調');
            onSuccess();
          } else {
            log.debug('LoginForm: 導航到 dashboard');
            navigate('/dashboard');
          }
        }, 100);
      } else {
        log.warn('LoginForm: 既沒有用戶也沒有錯誤，這很奇怪');
        setError('登入狀態異常，請稍後再試');
      }
    } catch (err) {
      log.error('LoginForm: 捕獲異常:', err);
      setError('登入時發生未知錯誤，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (error: any): string => {
    log.debug('LoginForm: 處理錯誤訊息:', error?.message);
    setSmartSuggestion(null); // 重置智慧建議
    
    if (error?.message?.includes('Invalid login credentials')) {
      log.debug('LoginForm: 檢測到無效登入憑證，顯示智慧建議');
      // 智慧建議：可能是 OAuth 用戶
      setSmartSuggestion('這個帳號可能是用 Google 註冊的，試試使用 Google 登入，或設定密碼以使用兩種登入方式。');
      return '電子郵件或密碼錯誤';
    }
    if (error?.message?.includes('Email not confirmed')) {
      log.debug('LoginForm: Email 未確認');
      return '請先驗證您的電子郵件';
    }
    if (error?.message?.includes('Too many requests')) {
      log.debug('LoginForm: 請求過於頻繁');
      return '嘗試次數過多，請稍後再試';
    }
    
    log.debug('LoginForm: 其他錯誤類型:', error?.message);
    return error?.message || '登入失敗，請稍後再試';
  };

  // 處理 Google 登入建議
  const handleGoogleLoginSuggestion = () => {
    setIsLoading(true);
    // 觸發 Google 登入
    document.querySelector<HTMLButtonElement>('button[data-google-login]')?.click();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-xl shadow-lg shadow-wedding-pink/20 p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-wedding-action/10 rounded-full mx-auto mb-4">
            <LogIn className="w-8 h-8 text-wedding-action" />
          </div>
          <h1 className="text-2xl font-bold text-wedding-text-title mb-2">歡迎回來</h1>
          <p className="text-wedding-text-body">登入到您的婚禮規劃平台</p>
        </div>

        {/* Google 登入按鈕 - 加回神奇的 div 包裝 */}
        <div className="mt-2">
          <button
            data-google-login
            onClick={async () => {
              log.debug('LoginForm: Google 登入 - 開始 OAuth 流程');
              try {
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                    queryParams: {
                      access_type: 'offline',
                      prompt: 'consent',
                    },
                  },
                });
                
                if (error) {
                  log.error('LoginForm: Google 登入錯誤:', error);
                  alert(`Google 登入失敗: ${error.message}`);
                } else {
                  log.info('LoginForm: Google OAuth 初始化成功');
                }
              } catch (err) {
                log.error('LoginForm: Google 登入未預期錯誤:', err);
                alert(`Google 登入錯誤: ${err}`);
              }
            }}
            className="w-full bg-white text-gray-700 border border-gray-300 px-4 py-3 rounded-md hover:bg-gray-50 font-medium"
            disabled={isLoading}
          >
            <svg className="inline-block w-5 h-5 mr-2 align-text-bottom" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            使用 Google 登入
          </button>
        </div>

        {/* 分隔線 */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">或</span>
          </div>
        </div>


        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-start">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-600 text-sm">{error}</p>
                  {smartSuggestion && (
                    <div className="mt-3 p-3 bg-wedding-action/5 border border-wedding-action/20 rounded-md">
                      <p className="text-wedding-text-body text-sm mb-2">{smartSuggestion}</p>
                      <button
                        type="button"
                        onClick={handleGoogleLoginSuggestion}
                        className="inline-flex items-center px-3 py-1 bg-wedding-action text-white text-xs rounded-md hover:bg-wedding-action-hover transition-colors"
                        disabled={isLoading}
                      >
                        <LogIn className="w-3 h-3 mr-1" />
                        使用 Google 登入
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-wedding-text-title mb-2">
              電子郵件
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-wedding-action focus:border-wedding-action transition-colors"
                placeholder="輸入您的電子郵件"
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-wedding-text-title mb-2">
              密碼
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-wedding-action focus:border-wedding-action transition-colors"
                placeholder="輸入您的密碼"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-wedding-action text-white py-3 rounded-md hover:bg-wedding-action-hover focus:ring-2 focus:ring-wedding-action focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                登入中...
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5 mr-2" />
                登入
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-wedding-text-body">
            還沒有帳號？{' '}
            <button
              onClick={onSignUpClick}
              className="text-wedding-action hover:text-wedding-action-hover font-medium transition-colors"
              disabled={isLoading}
            >
              立即註冊
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};